/**
 * IndexedDB Service - Capa de persistencia offline para Vortex EXANI-II
 * Maneja almacenamiento de preguntas, temas, áreas y sincronización con RTDB
 */

const DB_NAME = 'VortexEXANI_DB';
const DB_VERSION = 2;

// Esquema de almacenes
const STORES = {
    AREAS: 'areas',
    TEMAS: 'temas',
    PREGUNTAS_POR_TEMA: 'preguntas_por_tema',
    PREGUNTAS_LEGACY: 'preguntas_legacy',
    USER_QUIZZES: 'user_quizzes',
    USER_PROGRESS: 'user_progress',
    CACHE_META: 'cache_meta',
    PENDING_SYNC: 'pending_sync',
    QUIZ_RESULTS: 'quiz_results',
    SETTINGS: 'settings'
};

// Índices por store
const STORE_INDEXES = {
    [STORES.AREAS]: [{ name: 'id', keyPath: 'id', unique: true }],
    [STORES.TEMAS]: [
        { name: 'id', keyPath: 'id', unique: true },
        { name: 'area_id', keyPath: 'area_id', unique: false }
    ],
    [STORES.PREGUNTAS_POR_TEMA]: [
        { name: 'id', keyPath: 'id', unique: true },
        { name: 'tema_id', keyPath: 'tema_id', unique: false }
    ],
    [STORES.PREGUNTAS_LEGACY]: [
        { name: 'id', keyPath: 'id', unique: true },
        { name: 'tema_id', keyPath: 'tema_id', unique: false }
    ],
    [STORES.USER_QUIZZES]: [
        { name: 'id', keyPath: 'id', unique: true },
        { name: 'userId', keyPath: 'userId', unique: false },
        { name: 'areaId', keyPath: 'areaId', unique: false }
    ],
    [STORES.USER_PROGRESS]: [
        { name: 'id', keyPath: 'id', unique: true },
        { name: 'userId', keyPath: 'userId', unique: false },
        { name: 'temaId', keyPath: 'temaId', unique: false }
    ],
    [STORES.CACHE_META]: [
        { name: 'key', keyPath: 'key', unique: true }
    ],
    [STORES.PENDING_SYNC]: [
        { name: 'id', keyPath: 'id', unique: true },
        { name: 'type', keyPath: 'type', unique: false },
        { name: 'timestamp', keyPath: 'timestamp', unique: false }
    ],
    [STORES.QUIZ_RESULTS]: [
        { name: 'id', keyPath: 'id', unique: true },
        { name: 'userId', keyPath: 'userId', unique: false },
        { name: 'quizId', keyPath: 'quizId', unique: false },
        { name: 'timestamp', keyPath: 'timestamp', unique: false }
    ],
    [STORES.SETTINGS]: [
        { name: 'key', keyPath: 'key', unique: true }
    ]
};

class IndexedDBService {
    constructor() {
        this.db = null;
        this.initPromise = null;
        this.listeners = new Map();
    }

    // Inicializar base de datos
    async init() {
        if (this.initPromise) return this.initPromise;
        
        this.initPromise = new Promise((resolve, reject) => {
            if (typeof window === 'undefined' || !window.indexedDB) {
                reject(new Error('IndexedDB no soportado'));
                return;
            }

            const request = indexedDB.open(DB_NAME, DB_VERSION);

            request.onerror = () => reject(request.error);
            request.onsuccess = () => {
                this.db = request.result;
                this.db.onerror = (event) => console.error('[IndexedDB] Error:', event.target.error);
                resolve(this.db);
            };

            request.onupgradeneeded = (event) => {
                const db = event.target.result;
                const oldVersion = event.oldVersion;

                // Crear/actualizar stores
                Object.entries(STORES).forEach(([key, storeName]) => {
                    if (!db.objectStoreNames.contains(storeName)) {
                        const store = db.createObjectStore(storeName, { keyPath: 'id', autoIncrement: false });
                        // Agregar índices
                        if (STORE_INDEXES[storeName]) {
                            STORE_INDEXES[storeName].forEach(index => {
                                if (!store.indexNames.contains(index.name)) {
                                    store.createIndex(index.name, index.keyPath, { unique: index.unique });
                                }
                            });
                        }
                    } else if (event.oldVersion < DB_VERSION) {
                        // Migración de versión - actualizar índices si es necesario
                        const transaction = event.target.transaction;
                        const store = transaction.objectStore(storeName);
                        if (STORE_INDEXES[storeName]) {
                            STORE_INDEXES[storeName].forEach(index => {
                                if (!store.indexNames.contains(index.name)) {
                                    store.createIndex(index.name, index.keyPath, { unique: index.unique });
                                }
                            });
                        }
                    }
                });
            };
        });

        return this.initPromise;
    }

    // Verificar que la DB esté lista
    async ensureDb() {
        if (!this.db) {
            await this.init();
        }
        return this.db;
    }

    // Operaciones genéricas
    async get(storeName, key) {
        const db = await this.ensureDb();
        return new Promise((resolve, reject) => {
            const transaction = db.transaction(storeName, 'readonly');
            const store = transaction.objectStore(storeName);
            const request = store.get(key);
            request.onsuccess = () => resolve(request.result);
            request.onerror = () => reject(request.error);
        });
    }

    async getAll(storeName, indexName = null, query = null) {
        const db = await this.ensureDb();
        return new Promise((resolve, reject) => {
            const transaction = db.transaction(storeName, 'readonly');
            const store = transaction.objectStore(storeName);
            let request;
            
            if (indexName && query !== null) {
                const index = store.index(indexName);
                request = index.getAll(query);
            } else {
                request = store.getAll();
            }
            
            request.onsuccess = () => resolve(request.result);
            request.onerror = () => reject(request.error);
        });
    }

    async put(storeName, data) {
        const db = await this.ensureDb();
        return new Promise((resolve, reject) => {
            const transaction = db.transaction(storeName, 'readwrite');
            const store = transaction.objectStore(storeName);
            const request = store.put(data);
            request.onsuccess = () => {
                this.notifyListeners(storeName, 'put', data);
                resolve(request.result);
            };
            request.onerror = () => reject(request.error);
        });
    }

    async putAll(storeName, items) {
        const db = await this.ensureDb();
        return new Promise((resolve, reject) => {
            const transaction = db.transaction(storeName, 'readwrite');
            const store = transaction.objectStore(storeName);
            let completed = 0;
            let hasError = false;
            
            if (items.length === 0) {
                resolve([]);
                return;
            }
            
            items.forEach(item => {
                const request = store.put(item);
                request.onsuccess = () => {
                    completed++;
                    if (completed === items.length && !hasError) {
                        this.notifyListeners(storeName, 'putAll', items);
                        resolve(items);
                    }
                };
                request.onerror = () => {
                    if (!hasError) {
                        hasError = true;
                        reject(request.error);
                    }
                };
            });
        });
    }

    async delete(storeName, key) {
        const db = await this.ensureDb();
        return new Promise((resolve, reject) => {
            const transaction = db.transaction(storeName, 'readwrite');
            const store = transaction.objectStore(storeName);
            const request = store.delete(key);
            request.onsuccess = () => {
                this.notifyListeners(storeName, 'delete', key);
                resolve();
            };
            request.onerror = () => reject(request.error);
        });
    }

    async clear(storeName) {
        const db = await this.ensureDb();
        return new Promise((resolve, reject) => {
            const transaction = db.transaction(storeName, 'readwrite');
            const store = transaction.objectStore(storeName);
            const request = store.clear();
            request.onsuccess = () => {
                this.notifyListeners(storeName, 'clear', null);
                resolve();
            };
            request.onerror = () => reject(request.error);
        });
    }

    async count(storeName) {
        const db = await this.ensureDb();
        return new Promise((resolve, reject) => {
            const transaction = db.transaction(storeName, 'readonly');
            const store = transaction.objectStore(storeName);
            const request = store.count();
            request.onsuccess = () => resolve(request.result);
            request.onerror = () => reject(request.error);
        });
    }

    // Operaciones específicas para metadatos de caché
    async setCacheMeta(key, data, version = null) {
        const entry = {
            key,
            data,
            version,
            timestamp: Date.now(),
            updatedAt: new Date().toISOString()
        };
        return this.put(STORES.CACHE_META, entry);
    }

    async getCacheMeta(key) {
        return this.get(STORES.CACHE_META, key);
    }

    async isCacheValid(key, maxAgeMs = 24 * 60 * 60 * 1000) {
        const entry = await this.getCacheMeta(key);
        if (!entry) return false;
        return Date.now() - entry.timestamp <= maxAgeMs;
    }

    // Áreas
    async getAreas() {
        return this.getAll(STORES.AREAS);
    }

    async saveAreas(areas, version = null) {
        await this.putAll(STORES.AREAS, areas);
        if (version) await this.setCacheMeta('areas_version', { version }, version);
    }

    // Temas
    async getTemas() {
        return this.getAll(STORES.TEMAS);
    }

    async getTemasByArea(areaId) {
        return this.getAll(STORES.TEMAS, 'area_id', areaId);
    }

    async saveTemas(temas, version = null) {
        await this.putAll(STORES.TEMAS, temas);
        if (version) await this.setCacheMeta('temas_version', { version }, version);
    }

    // Preguntas por tema (nueva estructura RTDB)
    async getPreguntasPorTema(temaId) {
        return this.getAll(STORES.PREGUNTAS_POR_TEMA, 'tema_id', temaId);
    }

    async getAllPreguntasPorTema() {
        return this.getAll(STORES.PREGUNTAS_POR_TEMA);
    }

    async savePreguntasPorTema(preguntas, version = null) {
        // preguntas es un array de objetos con tema_id
        await this.putAll(STORES.PREGUNTAS_POR_TEMA, preguntas);
        if (version) await this.setCacheMeta('preguntas_por_tema_version', { version }, version);
    }

    async savePreguntasPorTemaMap(mapData, version = null) {
        // mapData viene como { temaId: { qId: preguntaData } }
        const preguntas = [];
        for (const [temaId, preguntasMap] of Object.entries(mapData)) {
            if (!preguntasMap) continue;
            for (const [qId, qData] of Object.entries(preguntasMap)) {
                if (!qData || typeof qData !== 'object') continue;
                preguntas.push({
                    id: qData.id || qId,
                    ...qData,
                    tema_id: parseInt(temaId)
                });
            }
        }
        await this.putAll(STORES.PREGUNTAS_POR_TEMA, preguntas);
        if (version) await this.setCacheMeta('preguntas_por_tema_version', { version }, version);
    }

    // Preguntas legacy (estructura antigua)
    async getPreguntasLegacy(temaId) {
        return this.getAll(STORES.PREGUNTAS_LEGACY, 'tema_id', temaId);
    }

    async savePreguntasLegacy(preguntas, version = null) {
        await this.putAll(STORES.PREGUNTAS_LEGACY, preguntas);
        if (version) await this.setCacheMeta('preguntas_legacy_version', { version }, version);
    }

    // Quizzes de usuario
    async getUserQuizzes(userId) {
        return this.getAll(STORES.USER_QUIZZES, 'userId', userId);
    }

    async saveUserQuiz(quiz) {
        return this.put(STORES.USER_QUIZZES, quiz);
    }

    async deleteUserQuiz(quizId) {
        return this.delete(STORES.USER_QUIZZES, quizId);
    }

    // Progreso de usuario
    async getUserProgress(userId, temaId = null) {
        if (temaId) {
            const all = await this.getAll(STORES.USER_PROGRESS, 'userId', userId);
            return all.find(p => p.temaId === temaId);
        }
        return this.getAll(STORES.USER_PROGRESS, 'userId', userId);
    }

    async saveUserProgress(progress) {
        return this.put(STORES.USER_PROGRESS, progress);
    }

    // Resultados de quiz (para sincronización offline)
    async saveQuizResult(result) {
        const entry = {
            id: result.id || `quiz_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
            ...result,
            timestamp: result.timestamp || Date.now(),
            synced: false
        };
        await this.put(STORES.QUIZ_RESULTS, entry);
        await this.addToPendingSync('quiz_result', entry.id, entry);
        return entry;
    }

    async getUnsyncedQuizResults(userId) {
        const all = await this.getAll(STORES.QUIZ_RESULTS, 'userId', userId);
        return all.filter(r => !r.synced);
    }

    async markQuizResultSynced(id) {
        const result = await this.get(STORES.QUIZ_RESULTS, id);
        if (result) {
            result.synced = true;
            result.syncedAt = Date.now();
            await this.put(STORES.QUIZ_RESULTS, result);
        }
    }

    // Cola de sincronización pendiente
    async addToPendingSync(type, entityId, payload) {
        const entry = {
            id: `sync_${type}_${entityId}_${Date.now()}`,
            type,
            entityId,
            payload,
            timestamp: Date.now(),
            retries: 0,
            status: 'pending'
        };
        return this.put(STORES.PENDING_SYNC, entry);
    }

    async getPendingSync(type = null) {
        if (type) {
            return this.getAll(STORES.PENDING_SYNC, 'type', type);
        }
        return this.getAll(STORES.PENDING_SYNC);
    }

    async markSyncComplete(id) {
        await this.delete(STORES.PENDING_SYNC, id);
    }

    async markSyncFailed(id, error) {
        const entry = await this.get(STORES.PENDING_SYNC, id);
        if (entry) {
            entry.retries++;
            entry.lastError = error?.message || String(error);
            entry.status = entry.retries >= 3 ? 'failed' : 'pending';
            await this.put(STORES.PENDING_SYNC, entry);
        }
    }

    // Configuración
    async getSetting(key) {
        const entry = await this.get(STORES.SETTINGS, key);
        return entry?.value;
    }

    async setSetting(key, value) {
        return this.put(STORES.SETTINGS, { key, value, updatedAt: new Date().toISOString() });
    }

    // Eventos
    on(storeName, event, callback) {
        const key = `${storeName}:${event}`;
        if (!this.listeners.has(key)) {
            this.listeners.set(key, new Set());
        }
        this.listeners.get(key).add(callback);
        return () => this.off(storeName, event, callback);
    }

    off(storeName, event, callback) {
        const key = `${storeName}:${event}`;
        const set = this.listeners.get(key);
        if (set) set.delete(callback);
    }

    notifyListeners(storeName, event, data) {
        const key = `${storeName}:${event}`;
        const set = this.listeners.get(key);
        if (set) {
            set.forEach(cb => {
                try { cb(data); } catch (e) { console.error('[IndexedDB] Listener error:', e); }
            });
        }
        // También notificar evento genérico
        const genericKey = `*:${event}`;
        const genericSet = this.listeners.get(genericKey);
        if (genericSet) {
            genericSet.forEach(cb => {
                try { cb({ store: storeName, data }); } catch (e) { console.error('[IndexedDB] Generic listener error:', e); }
            });
        }
    }

    // Limpieza
    async clearAllData() {
        const storeNames = Object.values(STORES);
        for (const storeName of storeNames) {
            await this.clear(storeName);
        }
    }

    async close() {
        if (this.db) {
            this.db.close();
            this.db = null;
            this.initPromise = null;
        }
    }
}

// Instancia singleton
const indexedDBService = new IndexedDBService();

export default indexedDBService;
export { STORES, DB_NAME, DB_VERSION };